'use client'

import {
  isConvertibleImageMediaType,
  isSvgMediaType,
  NORMALIZED_UPLOADS_PREFIX,
  UPLOAD_ORIGINALS_PREFIX,
  validateUserImageUpload,
} from '@/lib/core/media/image-upload-support'
import { observeBrowserOperation } from '@/lib/observability/browser-operation'
import { reportBrowserError } from '@/lib/observability/datadog-rum'
import { isOwnBlobUrlUnderPrefix } from '@/lib/shared/blob/owned-blob-url'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { newId } from '@/lib/shared/ids'
import { sleep } from '@/lib/shared/utils/sleep'
import { findAlreadyUploadedBatch } from './upload-content-dedupe'
import { isPermanentUploadError, UploadError } from './upload-error'
import {
  type UploadedUserFile,
  type UploadUserFileOptions,
  uploadUserFile,
} from './upload-user-file'

type IndexBrandImageArgs = {
  brandId: string
  image: UploadedUserFile
}

type IndexBrandImagesArgs = {
  brandId: string
  images: ReadonlyArray<UploadedUserFile>
  importId?: string
  batchId?: string
  requestDigest?: string
  /** More batches are coming under this `importId`. */
  expectMore?: boolean
  /** This is the last batch — close the import's queue. */
  seal?: boolean
  /** Files the browser intends to stage in total, across every batch. */
  declaredTotal?: number
  /** The library image this one file is an edited copy of. */
  editOf?: string
}

/**
 * What the server did with a batch. The browser MUST read this: `accepted: 0`
 * is a normal outcome (every file was already in the library) and used to be
 * rendered as an upload in flight, forever, with a success toast on top.
 */
export type BrandImageIndexResult = {
  accepted: number
  skipped: number
  skippedReasons?: {
    alreadyInLibrary: number
    duplicateName: number
    duplicateInSelection: number
    overCap: number
    unreachable: number
  }
  started: boolean
}

/**
 * Deadline for one batch submit.
 *
 * Generous — the route enqueues and may start a workflow — but finite, which is
 * the whole point: an unbounded submit stalls the chain behind it.
 */
const SUBMIT_TIMEOUT_MS = 45_000

async function readError(
  response: Response,
  fallback: string
): Promise<string> {
  const body = (await response.json().catch(() => ({}))) as { error?: unknown }
  return typeof body.error === 'string' ? body.error : fallback
}

class UploadRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterMs?: number
  ) {
    super(message)
    this.name = 'UploadRequestError'
  }
}

function retryAfterMs(response: Response): number | undefined {
  const raw = response.headers.get('Retry-After')
  if (!raw) {
    return
  }
  const seconds = Number(raw)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000
  }
  const date = Date.parse(raw)
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : undefined
}

async function requestError(
  response: Response,
  fallback: string
): Promise<UploadRequestError> {
  return new UploadRequestError(
    await readError(response, fallback),
    response.status,
    retryAfterMs(response)
  )
}

/** Validate, stage, optimize, and convert one local customer image. */
export async function uploadUserImage(
  file: File,
  contentLookup?: UploadedUserFile | null,
  options: UploadUserFileOptions = {}
): Promise<UploadedUserFile> {
  return observeBrowserOperation('upload.image.prepare', async () => {
    const validation = validateUserImageUpload(file)
    if (!validation.ok) {
      // Local verdict on the file itself — no attempt can change it.
      throw new UploadError('unsupported_image', validation.reason, {
        retryable: false,
      })
    }
    return contentLookup === undefined
      ? uploadUserFile(file, undefined, options)
      : uploadUserFile(file, contentLookup, options)
  })
}

/**
 * Optional brand-library tail: publish already-normalized images to the active
 * brand, then fan out caption/embed/index work in the background.
 *
 * Requests are sequential so receipts, progress, and sealing stay ordered.
 * Convex atomically appends each accepted batch; every asset receives its own
 * durable Workflow job without making the browser wait for search enrichment.
 */
async function indexUploadedBrandImages({
  brandId,
  images,
  importId,
  batchId,
  requestDigest,
  expectMore,
  seal,
  declaredTotal,
  editOf,
}: IndexBrandImagesArgs): Promise<BrandImageIndexResult> {
  return observeBrowserOperation(
    'upload.brand.submit',
    async () => {
      if (images.length === 0 && !seal) {
        return { accepted: 0, skipped: 0, started: false }
      }
      const response = await fetch('/api/brand-assets/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // `fetch` has NO default timeout. A request that black-holes — a mobile
        // handover, a captive portal, a proxy that accepts and never answers —
        // hangs until the tab closes, and because submits are chained that stalls
        // every batch behind it AND the seal. A deadline turns that into a retry.
        signal: AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
        body: JSON.stringify({
          brandId,
          ...(importId ? { importId } : {}),
          ...(batchId ? { batchId } : {}),
          ...(requestDigest ? { requestDigest } : {}),
          ...(expectMore ? { expectMore: true } : {}),
          ...(seal ? { seal: true } : {}),
          ...(declaredTotal ? { declaredTotal } : {}),
          ...(editOf ? { editOf } : {}),
          files: images.map((image) => ({
            // The name the CUSTOMER picked, not the stored one. `filename` is
            // truncated to 80 characters by the normalizer, so long names that
            // differ only in a trailing suffix collapse together — and filename
            // dedupe would then discard genuinely distinct images.
            name: image.originalFilename ?? image.filename,
            url: image.url,
            type: image.mediaType,
            // Measured by the normalizer. Without these the canonical processor's
            // passthrough branch leaves the library row dimensionless.
            ...(image.width ? { width: image.width } : {}),
            ...(image.height ? { height: image.height } : {}),
          })),
        }),
      })
      if (!response.ok) {
        throw await requestError(response, 'Could not add image to brand')
      }
      const body = (await response.json().catch(() => ({}))) as Partial<
        BrandImageIndexResult & { started: boolean }
      >
      return {
        accepted: typeof body.accepted === 'number' ? body.accepted : 0,
        skipped: typeof body.skipped === 'number' ? body.skipped : 0,
        ...(body.skippedReasons ? { skippedReasons: body.skippedReasons } : {}),
        started: body.started === true,
      }
    },
    { brandId, operationId: importId }
  )
}

/**
 * Create the server-scoped import. Started TOGETHER with staging (the hook
 * passes the pending promise as `importId`): the first CDN put does not wait
 * on this round trip, and the id is awaited only at the first batch submit.
 */
export async function createBrandImageImportSession(
  brandId: string,
  declaredTotal: number
): Promise<string> {
  return observeBrowserOperation(
    'upload.brand.session',
    async () => {
      const createRequestId = newId()
      const body = await withSubmitRetry(async () => {
        const response = await fetch('/api/brand-assets/import', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(SUBMIT_TIMEOUT_MS),
          body: JSON.stringify({
            action: 'create',
            brandId,
            declaredTotal,
            createRequestId,
          }),
        })
        if (!response.ok) {
          throw await requestError(response, 'Could not start the image upload')
        }
        return (await response.json()) as { importId?: unknown }
      })
      if (typeof body.importId !== 'string' || body.importId.length === 0) {
        throw new Error('Image upload session was not created')
      }
      return body.importId
    },
    { brandId }
  )
}

/**
 * Save an edited copy of a library image (Assets → Crop and adjust) as a NEW
 * image beside its original: the normalized upload lane (so its URL — and
 * its `?image=` key — never changes), then one import naming the original,
 * which files the copy in the original's bucket. `existing` means the exact
 * bytes are already in the library, so the caller can open that one.
 */
export async function saveEditedBrandImage({
  brandId,
  file,
  editOf,
  name,
}: {
  brandId: string
  file: File
  editOf: string
  /** The copy's name in the library ("Team photo (edited)"). */
  name: string
}): Promise<{ url: string; status: 'added' | 'existing' }> {
  const image = await uploadUserImage(file)
  const result = await indexUploadedBrandImages({
    brandId,
    images: [{ ...image, originalFilename: name }],
    editOf,
  })
  if (result.accepted > 0) {
    return { url: image.url, status: 'added' }
  }
  if ((result.skippedReasons?.alreadyInLibrary ?? 0) > 0) {
    return { url: image.url, status: 'existing' }
  }
  throw new Error('The edited image could not be added to your library')
}

/** Single-image convenience wrapper over {@link indexUploadedBrandImages}. */
export async function indexUploadedBrandImage({
  brandId,
  image,
}: IndexBrandImageArgs): Promise<BrandImageIndexResult> {
  return indexUploadedBrandImages({ brandId, images: [image] })
}

/**
 * ─── ADAPTIVE STAGING CONCURRENCY ───────────────────────────────────
 *
 * Each file costs a direct-to-Blob PUT (or a direct POST of its bytes for
 * anything under `DIRECT_NORMALIZE_MAX_BYTES`) plus a Sharp decode in its own
 * lambda, so an unbounded `Promise.all` over a 449-file selection would open
 * 449 sockets and 449 simultaneous decodes.
 *
 * A FIXED number is wrong in both directions, which is why this is a
 * controller rather than a constant. On office fibre, six parallel uploads
 * leaves most of the pipe idle; on hotel wifi, ten is what turns a slow upload
 * into a failing one — sockets time out, the pool retries, and the retries
 * compete with the originals for the same starved link. Staging is the only
 * thing the customer's tab is waiting on, and its duration is the window in
 * which an upload can still lose files, so the pool climbs while transfers
 * succeed and backs off hard the moment they stop.
 *
 * AIMD, the same shape TCP uses: additive increase, multiplicative decrease.
 * Failures are the signal, because a saturated link and an overloaded
 * normalizer both surface as errors long before they surface as slowness.
 */
const STAGING_CONCURRENCY_MIN = 2
const STAGING_CONCURRENCY_START = 6
const STAGING_CONCURRENCY_MAX = 16

/** Completions between adjustments. Small enough to react inside one wave. */
const ADAPT_WINDOW = 8

/**
 * Staged files per submit.
 *
 * Small enough that a dropped request costs little, large enough that a
 * 500-file selection is ~20 requests rather than 500. Must not exceed
 * `MAX_BRAND_IMPORT_BATCH`, which the route enforces.
 */
const SUBMIT_BATCH_SIZE = 25
/** First paint should wait for one normalized asset, not twenty-five. */
const FIRST_SUBMIT_BATCH_SIZE = 1

/** Matches the lookup route cap; the next window hashes while this one uploads. */
const CONTENT_LOOKUP_WINDOW_SIZE = 64

type AdaptivePoolStats = {
  /** Where the controller settled — surfaced for probes and telemetry. */
  finalConcurrency: number
  peakConcurrency: number
  backoffs: number
}

/**
 * Run `fn` over `items` with a self-tuning worker pool.
 *
 * Workers pull from a shared cursor and self-retire when the pool has shrunk
 * below the number currently running, so a backoff takes effect at the next
 * completion instead of waiting for the whole wave to drain.
 */
async function mapWithAdaptiveConcurrency<T, R>(
  items: ReadonlyArray<T>,
  fn: (item: T) => Promise<R>,
  onStats?: (stats: AdaptivePoolStats) => void
): Promise<Array<PromiseSettledResult<R>>> {
  const results: Array<PromiseSettledResult<R>> = new Array(items.length)
  if (items.length === 0) {
    return results
  }

  let cursor = 0
  let active = 0
  let completed = 0
  let target = Math.min(STAGING_CONCURRENCY_START, items.length)
  let peak = target
  let backoffs = 0
  let windowDone = 0
  let windowFailed = 0

  // Workers retire independently, so awaiting the initial set is not enough —
  // one that exits early says nothing about the ones spawned after it. Settle
  // on the item count instead, which is the only condition that actually means
  // "every file has an outcome".
  let signalDone: () => void = () => {}
  const allSettled = new Promise<void>((resolve) => {
    signalDone = resolve
  })

  const adjust = (): void => {
    windowDone++
    if (windowDone < ADAPT_WINDOW) {
      return
    }
    if (windowFailed > 0) {
      // Multiplicative decrease. One failure in a window is enough: at this
      // point retries are already competing with fresh uploads for the same
      // link, and adding more of either makes it worse.
      target = Math.max(STAGING_CONCURRENCY_MIN, Math.floor(target / 2))
      backoffs++
    } else {
      target = Math.min(STAGING_CONCURRENCY_MAX, target + 2)
      peak = Math.max(peak, target)
    }
    windowDone = 0
    windowFailed = 0
  }

  // biome-ignore-start lint/performance/noAwaitInLoops: worker pulls from a shared queue
  const worker = async (): Promise<void> => {
    active++
    try {
      for (;;) {
        // Retire if the controller has shrunk the pool beneath us. Checked
        // BEFORE claiming an index so a retiring worker never strands an item.
        if (active > target) {
          return
        }
        const index = cursor++
        if (index >= items.length) {
          return
        }
        try {
          results[index] = {
            status: 'fulfilled',
            value: await fn(items[index]),
          }
        } catch (reason) {
          results[index] = { status: 'rejected', reason }
          windowFailed++
        }
        completed++
        if (completed === items.length) {
          signalDone()
        }
        adjust()
        // Grow into the new target. Spawned from here rather than a central
        // loop so the pool only expands while work is actually flowing.
        // `worker()` runs synchronously up to its first await, so `active` is
        // already incremented by the time this condition is re-tested.
        while (active < target && cursor < items.length) {
          void worker()
        }
      }
    } finally {
      active--
    }
  }
  // biome-ignore-end lint/performance/noAwaitInLoops: migrated from eslint suppression

  for (let i = 0; i < target; i++) {
    void worker()
  }
  await allSettled

  onStats?.({ finalConcurrency: target, peakConcurrency: peak, backoffs })
  return results
}

export type BrandImageUploadOptions = {
  /**
   * Correlation id shared with the server so staging progress and indexing
   * progress land on ONE bell notification — and so every batch joins the same
   * durable queue.
   *
   * May still be in flight: the id is only needed when the FIRST batch is
   * submitted, so callers start the session create and staging together
   * instead of spending a round trip before the first CDN put. A rejected
   * promise fails every submit (reported as failed batches); staging itself
   * never waits on it.
   */
  importId?: string | Promise<string>
  /**
   * Called as each file finishes staging. Throttle inside the callback — this
   * fires once per file, and for a 449-file selection an unthrottled Convex
   * write per call would hammer a single row.
   *
   * `bytes`/`totalBytes` make the bar honest on a mixed selection: counting
   * FILES puts a 20MB RAW and a 40KB icon at the same weight, so a bar built on
   * the file count races to 90% and then appears to hang on the big ones.
   */
  onStaged?: (
    staged: number,
    total: number,
    bytes?: number,
    totalBytes?: number
  ) => void
  /**
   * Called with each file's staged upload, before its batch is submitted.
   * `uploaded.url` is the URL the file's library row carries — as `url` on
   * the placeholder card, then as `sourceUrl` once the drainer converts it —
   * so a caller can tell when that file's row has landed.
   */
  onFileStaged?: (file: File, uploaded: UploadedUserFile) => void
  /**
   * Called after each batch reaches the server, with the RUNNING totals of what
   * the server accepted and skipped. This is the only honest source for "how
   * many images am I actually adding" — the staged count includes files the
   * library already had.
   */
  onSubmitted?: (progress: BrandImageSubmitProgress) => void
}

type BrandImageSubmitProgress = {
  submitted: number
  accepted: number
  skipped: number
  skippedReasons: {
    alreadyInLibrary: number
    duplicateName: number
    duplicateInSelection: number
    overCap: number
    unreachable: number
  }
  /** Batches that could not be delivered at all. */
  failedBatches: number
  /**
   * Files staged by an older/lookup path but rejected by the normalized
   * brand-import contract. Current brand staging fails normalization before a
   * queue batch is built.
   */
  failedFiles: number
}

/**
 * Whether a staged file can enter a brand library.
 *
 * Mirrors the import route's gate. Kept as a client-side predicate so a file
 * that cannot pass is dropped from its batch instead of failing the batch: the
 * route is deliberately strict (an unnormalized URL there means the SSRF gate
 * fired), and being strict is only safe if the client never sends one by
 * accident.
 */
function isImportableBrandImage(file: UploadedUserFile): boolean {
  if (isOwnBlobUrlUnderPrefix(file.url, NORMALIZED_UPLOADS_PREFIX)) {
    return file.mediaType === 'image/png' || file.mediaType === 'image/gif'
  }
  // The fast lane: a staged original the drainer will normalize.
  if (isOwnBlobUrlUnderPrefix(file.url, UPLOAD_ORIGINALS_PREFIX)) {
    return (
      isConvertibleImageMediaType(file.mediaType) &&
      !isSvgMediaType(file.mediaType)
    )
  }
  return false
}

export type BrandImageUploadOutcome = {
  /** Staging results in input order — callers apply one to an element. */
  staged: Array<PromiseSettledResult<UploadedUserFile>>
  submit: BrandImageSubmitProgress
}

/**
 * Attempts per batch submit. The server stores a receipt keyed by
 * `(importId, batchId)`, including the canonical request digest and exact
 * response, so a lost HTTP response is safe to replay without re-enqueueing or
 * changing the counters.
 */
const SUBMIT_ATTEMPTS = 3
const SUBMIT_RETRY_DELAY_MS = 700

async function withSubmitRetry<T>(fn: () => Promise<T>): Promise<T> {
  // biome-ignore-start lint/performance/noAwaitInLoops: retries are sequential by definition
  let lastError: unknown
  // biome-ignore lint/plugin: retry-loop: allowed — honours the server's Retry-After on an idempotent POST, which the shared helper only does for safe reads
  for (let attempt = 1; attempt <= SUBMIT_ATTEMPTS; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      // A 4xx describes the payload and cannot improve on a retry; anything
      // else (5xx, offline, DNS) is a moment, not a verdict.
      const message = getErrorMessage(error, String(error))
      const isPermanent =
        error instanceof UploadRequestError
          ? error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408 &&
            error.status !== 429
          : /must be a normalized|must be image\/|Forbidden|Unauthorized|Brand not found|At most/i.test(
              message
            )
      if (attempt === SUBMIT_ATTEMPTS || isPermanent) {
        break
      }
      const serverDelay =
        error instanceof UploadRequestError ? error.retryAfterMs : undefined
      await sleep(Math.max(SUBMIT_RETRY_DELAY_MS * attempt, serverDelay ?? 0))
    }
  }
  // biome-ignore-end lint/performance/noAwaitInLoops: migrated from eslint suppression
  throw lastError
}

function emptySubmitProgress(): BrandImageSubmitProgress {
  return {
    submitted: 0,
    accepted: 0,
    skipped: 0,
    skippedReasons: {
      alreadyInLibrary: 0,
      duplicateName: 0,
      duplicateInSelection: 0,
      overCap: 0,
      unreachable: 0,
    },
    failedBatches: 0,
    failedFiles: 0,
  }
}

/**
 * Stage a selection to Blob as fast as the pipe allows, handing each batch to
 * the server the moment it is ready.
 *
 * ─── WHY BATCHES AND NOT ONE SUBMIT ─────────────────────────────────
 *
 * The previous shape staged all N files and then made a single request. Nothing
 * was durable until the last file finished, so a 449-file upload had a
 * multi-minute window in which closing the tab destroyed every staged blob —
 * prod recorded a customer losing 37 of them — and no image could appear in the
 * grid until staging was completely done.
 *
 * Now: stage at `STAGING_CONCURRENCY`, submit every `SUBMIT_BATCH_SIZE` files,
 * seal at the end. Processing starts seconds in, and an interrupted upload
 * keeps everything already submitted.
 *
 * SUBMITS ARE SEQUENTIAL, ON PURPOSE. This preserves receipt order, avoids a
 * burst against the API rate limiter, and keeps the declared progress easy to
 * reconcile. The server's brand lease—not browser request ordering—is the
 * authority that prevents concurrent brand-row writers. Staging continues at
 * full speed while a submit is in flight.
 */
export async function uploadAndIndexBrandImages(
  files: ReadonlyArray<File>,
  brandId: string | undefined,
  options: BrandImageUploadOptions = {}
): Promise<BrandImageUploadOutcome> {
  return observeBrowserOperation(
    'upload.brand.batch',
    async () => {
      const progress = emptySubmitProgress()
      const pending: Array<UploadedUserFile> = []
      let hasSubmittedFirstAsset = false
      // One submit at a time: the tail of this chain is the only in-flight request.
      let submitChain: Promise<void> = Promise.resolve()
      // Resolved once, awaited by every submit. Wrapping a plain string keeps the
      // chain uniform; a caller's in-flight create simply lands here later.
      const importIdSource =
        options.importId === undefined
          ? undefined
          : Promise.resolve(options.importId)
      // A rejection is consumed by the submit chain; this side branch only stops
      // the browser reporting it as unhandled in the gap before the first submit.
      importIdSource?.catch(() => {})

      const submit = (
        rawBatch: Array<UploadedUserFile>,
        seal: boolean
      ): void => {
        if (!(brandId && importIdSource)) {
          return
        }

        // ONE BAD FILE MUST NOT POISON ITS BATCH.
        //
        // The import route validates every entry and 400s the WHOLE request if any
        // one fails — correctly, since an unnormalized URL is the SSRF gate firing.
        // Cached clients and stale content-index entries can still hand us a shape
        // that predates normalized-only brand staging. Submitting it alongside 24
        // healthy files would lose all 25 to a rejection none of them caused.
        //
        // Filter here, where we still know which file is which, and report the
        // dropped ones as failures rather than letting them disappear.
        const batch = rawBatch.filter(isImportableBrandImage)
        const rejected = rawBatch.length - batch.length
        if (rejected > 0) {
          progress.failedFiles += rejected
        }

        if (batch.length === 0 && !seal) {
          return
        }
        const batchId = newId()
        const requestDigest = JSON.stringify(
          batch.map((image) => [
            image.url,
            image.originalFilename ?? image.filename,
            image.mediaType,
            image.width ?? null,
            image.height ?? null,
          ])
        )
        submitChain = submitChain.then(async () => {
          let importId: string | undefined
          try {
            importId = await importIdSource
            // Retried against the batch receipt: a transient 5xx after Convex
            // commits returns the exact original response instead of enqueueing or
            // counting the files twice.
            const result = await withSubmitRetry(() =>
              indexUploadedBrandImages({
                brandId,
                images: batch,
                importId,
                batchId,
                requestDigest,
                declaredTotal: files.length,
                // Hold the queue open until the last batch, so the drainer waits
                // for the rest of the selection instead of declaring the import
                // done.
                ...(seal ? { seal: true } : { expectMore: true }),
              })
            )
            progress.submitted += batch.length
            progress.accepted += result.accepted
            progress.skipped += result.skipped
            if (result.skippedReasons) {
              for (const key of Object.keys(progress.skippedReasons) as Array<
                keyof BrandImageSubmitProgress['skippedReasons']
              >) {
                progress.skippedReasons[key] += result.skippedReasons[key] ?? 0
              }
            }
          } catch (error) {
            // A failed batch must not abort the ones behind it — they are separate
            // files, already staged, and the customer would rather have most of
            // their upload than none of it.
            // The zero-file seal is a control write, not a failed file batch.
            if (batch.length > 0) {
              progress.failedBatches++
            }
            reportBrowserError(error, {
              action: 'brand_image_import_submit',
              brandId,
              importId,
              batchId,
              isSeal: batch.length === 0,
              fileCount: batch.length,
            })
          }
          options.onSubmitted?.({ ...progress })
        })
      }

      let stagedCount = 0
      let stagedBytes = 0
      const totalBytes = files.reduce((sum, file) => sum + (file.size || 0), 0)

      const stageOne = async (
        file: File,
        /**
         * The retry sweep re-stages a file that already counted toward progress.
         * Counting it twice would push the bar past 100% and, because the hook
         * derives its percentage from this, make a recovering upload look broken.
         */
        isRetry = false,
        contentLookup?: UploadedUserFile | null
      ): Promise<UploadedUserFile> => {
        try {
          const uploaded = await uploadUserImage(file, contentLookup, {
            mode: 'brand-import',
          })
          try {
            options.onFileStaged?.(file, uploaded)
          } catch (error) {
            // Advisory, like `onStaged`: a throwing callback must not fail a
            // file that staged.
            reportBrowserError(error, {
              action: 'brand_image_import_file_staged_callback',
              brandId,
            })
          }
          pending.push(uploaded)
          const submitSize = hasSubmittedFirstAsset
            ? SUBMIT_BATCH_SIZE
            : FIRST_SUBMIT_BATCH_SIZE
          if (pending.length >= submitSize) {
            submit(pending.splice(0, submitSize), false)
            hasSubmittedFirstAsset = true
          }
          return uploaded
        } finally {
          if (!isRetry) {
            stagedCount += 1
            stagedBytes += file.size || 0
            try {
              options.onStaged?.(
                stagedCount,
                files.length,
                stagedBytes,
                totalBytes
              )
            } catch (error) {
              // A throwing progress callback must not fail the FILE. Without this
              // it escapes the `finally`, replaces the return value, and a
              // perfectly good upload is recorded as rejected — then re-uploaded by
              // the retry sweep for no reason.
              reportBrowserError(error, {
                action: 'brand_image_import_progress_callback',
                brandId,
                // May still be a pending promise here; the batch submit logs
                // the resolved id, so a string is all this context needs.
                importId:
                  typeof options.importId === 'string'
                    ? options.importId
                    : undefined,
              })
            }
          }
        }
      }

      const staged: Array<PromiseSettledResult<UploadedUserFile>> = new Array(
        files.length
      )
      const contentLookups: Array<UploadedUserFile | null> = new Array(
        files.length
      )

      // Pipeline fixed lookup windows with staging. Hashing the whole 500-file
      // selection before the first upload would make a fast dedupe path feel slow;
      // hashing files independently from each staging worker degenerates into
      // hundreds of lookup requests on slow devices. This shape begins staging
      // after the first 64 hashes and hides the next lookup behind active uploads.
      const lookupWindows: Array<{
        start: number
        files: ReadonlyArray<File>
      }> = []
      if (files.length > 0) {
        lookupWindows.push({ start: 0, files: files.slice(0, 1) })
        for (
          let start = 1;
          start < files.length;
          start += CONTENT_LOOKUP_WINDOW_SIZE
        ) {
          lookupWindows.push({
            start,
            files: files.slice(start, start + CONTENT_LOOKUP_WINDOW_SIZE),
          })
        }
      }
      let nextLookup = findAlreadyUploadedBatch(lookupWindows[0]?.files ?? [])
      // biome-ignore-start lint/performance/noAwaitInLoops: windows intentionally pipeline in order
      for (
        let windowIndex = 0;
        windowIndex < lookupWindows.length;
        windowIndex++
      ) {
        const { start, files: window } = lookupWindows[windowIndex]
        const known = await nextLookup
        for (let offset = 0; offset < known.length; offset++) {
          contentLookups[start + offset] = known[offset]
        }

        const following = lookupWindows[windowIndex + 1]
        if (following) {
          nextLookup = findAlreadyUploadedBatch(following.files)
        }

        const windowResults = await mapWithAdaptiveConcurrency(
          window.map((file, offset) => ({ file, lookup: known[offset] })),
          ({ file, lookup }) => stageOne(file, false, lookup)
        )
        for (let offset = 0; offset < windowResults.length; offset++) {
          staged[start + offset] = windowResults[offset]
        }
      }
      // biome-ignore-end lint/performance/noAwaitInLoops: migrated from eslint suppression

      // ONE MORE PASS FOR THE FILES THAT FAILED — TRANSIENTLY.
      //
      // A file whose staging exhausted its own retries used to be gone — reported
      // in a toast and never attempted again, even when the cause was a transient
      // blip that has since cleared. The per-file retry inside `uploadUserFile`
      // fires within seconds of the failure, so it is competing with exactly the
      // conditions that caused it; this sweep runs after the wave has drained, on
      // a quiet link, at minimum concurrency. Anything it recovers is a file the
      // customer would otherwise have had to notice and re-pick by hand.
      //
      // A file the SERVER refused (not an image, conversion out of time) is not
      // re-staged: the verdict is deterministic for those bytes, and re-running a
      // 60s GIF encode here is what kept the customer's spinner up for minutes.
      const failedIndexes = staged
        .map((result, index) =>
          result.status === 'rejected' && !isPermanentUploadError(result.reason)
            ? index
            : -1
        )
        .filter((index) => index >= 0)

      if (failedIndexes.length > 0) {
        // biome-ignore-start lint/performance/noAwaitInLoops: the sweep is deliberately serial
        for (const index of failedIndexes) {
          try {
            staged[index] = {
              status: 'fulfilled',
              value: await stageOne(files[index], true, contentLookups[index]),
            }
          } catch {
            // Leave the original rejection in place; the caller reports it.
          }
        }
        // biome-ignore-end lint/performance/noAwaitInLoops: migrated from eslint suppression
      }

      // Keep the last data receipt independent from the seal. If the server
      // commits that batch but its response is lost, replaying the stable batch id
      // recovers the exact acceptance result; if the batch still cannot be
      // delivered, the following control request can nevertheless close the
      // session with an authoritative client-unsubmitted count.
      submit(pending.splice(0, pending.length), false)
      // Sealing is what turns "the queue is empty" into "the import is finished".
      // A separate zero-file request is idempotent and remains deliverable even
      // when the final data batch exhausted its own retries.
      submit([], true)
      await submitChain

      // Callers that never opted into the durable path (no `importId`) still get
      // their images into the library through the legacy single-shot submit.
      if (brandId && options.importId === undefined) {
        const uploaded = staged
          .filter(
            (result): result is PromiseFulfilledResult<UploadedUserFile> =>
              result.status === 'fulfilled'
          )
          .map((result) => result.value)
        if (uploaded.length > 0) {
          const result = await indexUploadedBrandImages({
            brandId,
            images: uploaded,
          })
          progress.submitted += uploaded.length
          progress.accepted += result.accepted
          progress.skipped += result.skipped
        }
      }

      return { staged, submit: progress }
    },
    { brandId }
  )
}

/** Fetch a public image link through the SSRF-safe server normalizer. */
export async function importUserImageUrl(
  url: string
): Promise<UploadedUserFile> {
  return observeBrowserOperation('upload.image.import', async () => {
    const response = await fetch('/api/upload/import-image', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    })
    if (!response.ok) {
      throw await requestError(response, 'Could not import image URL')
    }
    return (await response.json()) as UploadedUserFile
  })
}

export async function importAndIndexBrandImageUrl(
  url: string,
  brandId: string
): Promise<UploadedUserFile> {
  const image = await importUserImageUrl(url)
  const result = await indexUploadedBrandImage({ brandId, image })
  if (result.accepted === 0) {
    const isAlreadyInLibrary =
      (result.skippedReasons?.alreadyInLibrary ?? 0) +
        (result.skippedReasons?.duplicateName ?? 0) >
      0
    throw new Error(
      isAlreadyInLibrary
        ? 'That image is already in your library'
        : 'The image could not be added to your library'
    )
  }
  return image
}
